import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import { useAuth } from '@src/context';
import { fetchClient } from '@src/api/actions/clients';
import HistoryIcon from '@mui/icons-material/History';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  fetchPresets,
  listWebhookConfigs,
  createWebhookConfig,
  updateWebhookConfig,
  testWebhookConfig,
  listArchivedWebhookConfigs,
  promoteWebhookConfig,
  testAuthWebhookConfig,
} from '@src/api/actions/webhookConfig';
import {
  listEmailNotifications,
  createEmailNotification,
  updateEmailNotification,
  deleteEmailNotification,
} from '@src/api/actions/emailNotifications';

const CHANNELS = [
  { value: 'primary', label: 'Primary Webhook', desc: 'Where all leads are sent (regular and self-book)', required: true },
  { value: 'selfbook', label: 'Self-Book Override', desc: 'Only needed if self-book leads go to a different URL or need different field mapping. Most clients do NOT need this.', required: false },
  { value: 'secondary', label: 'Secondary Webhook', desc: 'Additional destination for lead copies, e.g., Hatch, secondary CRM (optional)', required: false },
  { value: 'tertiary', label: 'Third Webhook', desc: 'Additional destination (optional)', required: false },
];

export default function WebhookSetupPage() {
  const { clientSlug: paramSlug } = useParams();
  const { state: authState } = useAuth();
  const isStaff = authState?.user?.is_staff || authState?.user?.is_superuser;
  const [selectedSlug, setSelectedSlug] = useState(paramSlug || '');
  const clientSlug = paramSlug || selectedSlug;
  const isStaffView = !paramSlug && isStaff;

  const [client, setClient] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [presets, setPresets] = useState([]);
  const [leadFields, setLeadFields] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [clientVerticals, setClientVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testDialog, setTestDialog] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showSelfBook, setShowSelfBook] = useState(false);
  const [showTertiary, setShowTertiary] = useState(false);
  const [emailConfigs, setEmailConfigs] = useState([]);
  // Staging mode: track which mode each channel is viewing
  const [channelViewMode, setChannelViewMode] = useState({});
  const [archivedConfigs, setArchivedConfigs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);

  // Load all clients for staff dropdown
  useEffect(() => {
    if (isStaffView) {
      import('@src/api/actions/clients').then(({ fetchClients }) => {
        fetchClients().then((res) => {
          const clients = (res?.data?.results || res?.data || [])
            .filter((c) => c.active !== false)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setAllClients(clients);
          if (!selectedSlug && clients.length) {
            setSelectedSlug(clients[0].slug);
          }
        }).catch(() => {});
      });
    }
  }, [isStaffView]);

  useEffect(() => {
    if (!clientSlug) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      fetchClient(clientSlug).catch(() => null),
      fetchPresets().catch(() => null),
      listWebhookConfigs(clientSlug).catch(() => null),
      listEmailNotifications(clientSlug).catch(() => null),
    ]).then(([clientRes, presetsRes, configsRes, emailRes]) => {
      if (clientRes?.data) setClient(clientRes.data);
      if (presetsRes?.data) {
        setPresets(presetsRes.data.presets || []);
        setLeadFields(presetsRes.data.lead_fields || []);
      }
      if (configsRes?.data) {
        setConfigs(configsRes.data.results || []);
        setClientVerticals(configsRes.data.client_verticals || []);
      }
      if (emailRes?.data) setEmailConfigs(emailRes.data.results || []);
    }).finally(() => setLoading(false));
  }, [clientSlug]);

  const handleSaveChannel = async (channelData) => {
    try {
      const res = await createWebhookConfig(clientSlug, channelData);
      toast.success(`${channelData.channel} webhook saved!`);
      const refreshed = await listWebhookConfigs(clientSlug);
      setConfigs(refreshed?.data?.results || []);
      return res?.data;
    } catch (err) {
      toast.error('Failed to save webhook config');
      return null;
    }
  };

  const handlePromote = async (configId, channelValue) => {
    try {
      await promoteWebhookConfig(clientSlug, configId);
      toast.success('Configuration promoted to live!');
      const refreshed = await listWebhookConfigs(clientSlug);
      setConfigs(refreshed?.data?.results || []);
      setChannelViewMode((prev) => ({ ...prev, [channelValue]: 'live' }));
    } catch (err) {
      toast.error('Failed to promote configuration');
    }
  };

  const loadArchivedConfigs = async () => {
    setLoadingArchived(true);
    try {
      const res = await listArchivedWebhookConfigs(clientSlug);
      setArchivedConfigs(res?.data?.results || []);
    } catch {
      setArchivedConfigs([]);
    } finally {
      setLoadingArchived(false);
    }
  };

  const [testPayloadStr, setTestPayloadStr] = useState('');

  const handleTest = async (configId) => {
    setTestDialog(configId);
    setTestResult(null);
    setTesting(false);
    // First call with no payload to get the default generated payload
    try {
      const res = await testWebhookConfig(clientSlug, configId, { dry_run: true });
      setTestPayloadStr(JSON.stringify(res?.data?.payload_sent || {}, null, 2));
    } catch {
      setTestPayloadStr('{}');
    }
  };

  const handleSendTest = async () => {
    if (!testDialog) return;
    setTesting(true);
    setTestResult(null);
    try {
      let payload;
      try { payload = JSON.parse(testPayloadStr); } catch { toast.error('Invalid JSON in payload'); setTesting(false); return; }
      const res = await testWebhookConfig(clientSlug, testDialog, { custom_payload: payload });
      setTestResult(res?.data);
    } catch (err) {
      setTestResult({
        success: false,
        status_code: err?.response?.status,
        response_body: err?.response?.data?.response_body || 'Request failed',
        payload_sent: {},
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress size={48} /></Box>;
  }

  const clientName = client?.dba_name || client?.name || clientSlug;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Webhook Setup | {clientName} | {config.SITE.title}</title></Helmet>

      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>{clientName}</Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Staff client selector */}
        {isStaffView && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={700}>Lead Delivery</Typography>
            <TextField
              select
              size="small"
              value={selectedSlug}
              onChange={(e) => { setSelectedSlug(e.target.value); setConfigs([]); setClient(null); }}
              sx={{ minWidth: 250 }}
              label="Client"
            >
              {allClients.map((c) => (
                <MenuItem key={c.slug || c.id} value={c.slug}>{c.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {!isStaffView && (
          <Typography variant="h4" fontWeight={700} gutterBottom>Lead Delivery Setup</Typography>
        )}
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          Configure how leads are delivered to {clientName ? `${clientName}'s` : 'your'} CRM or lead management system.
        </Typography>
        <Alert severity="info" sx={{ mb: 4 }}>
          Set up your primary webhook to start receiving leads. Self-book and secondary webhooks are optional.
        </Alert>

        {!clientSlug && isStaffView && (
          <Alert severity="warning" sx={{ mb: 2 }}>Select a client above to manage their webhook configuration.</Alert>
        )}

        {clientSlug && <>
        {/* Primary — always shown */}
        <ChannelWithStaging
          channel={CHANNELS[0]}
          configs={configs}
          presets={presets}
          leadFields={leadFields}
          clientVerticals={clientVerticals}
          onSave={handleSaveChannel}
          onTest={handleTest}
          onPromote={handlePromote}
          viewMode={channelViewMode['primary'] || 'live'}
          onViewModeChange={(m) => setChannelViewMode((prev) => ({ ...prev, primary: m }))}
          clientSlug={clientSlug}
          isStaff={isStaff}
        />

        {/* Secondary — always shown */}
        <ChannelWithStaging
          channel={CHANNELS[2]}
          configs={configs}
          presets={presets}
          leadFields={leadFields}
          clientVerticals={clientVerticals}
          onSave={handleSaveChannel}
          onTest={handleTest}
          onPromote={handlePromote}
          viewMode={channelViewMode['secondary'] || 'live'}
          onViewModeChange={(m) => setChannelViewMode((prev) => ({ ...prev, secondary: m }))}
          clientSlug={clientSlug}
          isStaff={isStaff}
        />

        {/* Self-Book Override — hidden, rare */}
        {showSelfBook || configs.find((c) => c.channel === 'selfbook' && c.mode !== 'archived')?.webhook_url ? (
          <ChannelWithStaging
            channel={CHANNELS[1]}
            configs={configs}
            presets={presets}
            leadFields={leadFields}
            clientVerticals={clientVerticals}
            onSave={handleSaveChannel}
            onTest={handleTest}
            onPromote={handlePromote}
            viewMode={channelViewMode['selfbook'] || 'live'}
            onViewModeChange={(m) => setChannelViewMode((prev) => ({ ...prev, selfbook: m }))}
            clientSlug={clientSlug}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <Button size="small" sx={{ textTransform: 'none', fontSize: 12, color: 'text.disabled' }}
              onClick={() => setShowSelfBook(true)}>
              Need a separate webhook for self-book leads? (rare)
            </Button>
          </Box>
        )}

        {/* Tertiary — hidden until needed */}
        {showTertiary || configs.find((c) => c.channel === 'tertiary' && c.mode !== 'archived')?.webhook_url ? (
          <ChannelWithStaging
            channel={CHANNELS[3]}
            configs={configs}
            presets={presets}
            leadFields={leadFields}
            clientVerticals={clientVerticals}
            onSave={handleSaveChannel}
            onTest={handleTest}
            onPromote={handlePromote}
            viewMode={channelViewMode['tertiary'] || 'live'}
            onViewModeChange={(m) => setChannelViewMode((prev) => ({ ...prev, tertiary: m }))}
            clientSlug={clientSlug}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <Button size="small" sx={{ textTransform: 'none', fontSize: 12, color: 'text.disabled' }}
              onClick={() => setShowTertiary(true)}>
              Need a third webhook destination?
            </Button>
          </Box>
        )}

        {/* Archived Configs Section */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => { setShowArchived(!showArchived); if (!showArchived) loadArchivedConfigs(); }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {showArchived ? 'Hide' : 'View'} Archived Configurations
          </Button>
          {showArchived && (
            <Box sx={{ mt: 2 }}>
              {loadingArchived ? (
                <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : archivedConfigs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No archived configurations.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {archivedConfigs.map((ac) => (
                    <Paper key={ac.id} variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2">{ac.channel_display}</Typography>
                            <Chip label={ac.label || ac.crm_preset_display} size="small" variant="outlined" />
                            <Chip label="Archived" size="small" color="default" />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {ac.webhook_url} {ac.archived_at ? `— archived ${new Date(ac.archived_at).toLocaleDateString()}` : ''}
                          </Typography>
                        </Box>
                        <Tooltip title="Restore to live (archives current live config)">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestoreIcon />}
                            onClick={() => handlePromote(ac.id, ac.channel)}
                            sx={{ textTransform: 'none' }}
                          >
                            Restore
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>

        {/* Email Notifications Section */}
        <Divider sx={{ my: 4 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>Email Notifications</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Configure who receives email notifications when leads come in. Add conditions to route notifications to specific people based on lead data.
        </Typography>

        {emailConfigs.map((ec) => (
          <Card key={ec.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Name" size="small" value={ec.name}
                    onChange={(e) => setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, name: e.target.value } : c))}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Email" size="small" type="email" value={ec.email}
                    onChange={(e) => setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, email: e.target.value } : c))}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" color="error" onClick={async () => {
                    await deleteEmailNotification(clientSlug, ec.id);
                    setEmailConfigs((prev) => prev.filter((c) => c.id !== ec.id));
                    toast.success('Notification removed');
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack direction="row" spacing={3} alignItems="center">
                  <FormControlLabel
                    control={<Switch size="small" checked={ec.notify_standard_leads}
                      onChange={(e) => setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, notify_standard_leads: e.target.checked } : c))} />}
                    label={<Typography variant="body2">Standard leads</Typography>}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={ec.notify_self_book_leads}
                      onChange={(e) => setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, notify_self_book_leads: e.target.checked } : c))} />}
                    label={<Typography variant="body2">Self-book leads</Typography>}
                  />
                </Stack>

                {/* Conditions */}
                {(ec.conditions || []).length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Only notify when:</Typography>
                    {ec.conditions.map((cond, ci) => (
                      <Stack key={ci} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <TextField
                          select size="small" value={cond.field || ''}
                          onChange={(e) => {
                            const updated = [...(ec.conditions || [])];
                            updated[ci] = { ...updated[ci], field: e.target.value };
                            setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                          }}
                          sx={{ minWidth: 130 }}
                        >
                          <MenuItem value="territory">Territory</MenuItem>
                          <MenuItem value="vertical">Vertical</MenuItem>
                          <MenuItem value="project_type">Project Type</MenuItem>
                        </TextField>
                        <Typography variant="body2">=</Typography>
                        <TextField
                          size="small" value={cond.value || ''} placeholder="Value"
                          onChange={(e) => {
                            const updated = [...(ec.conditions || [])];
                            updated[ci] = { ...updated[ci], value: e.target.value };
                            setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                          }}
                          sx={{ flex: 1 }}
                        />
                        <IconButton size="small" onClick={() => {
                          const updated = (ec.conditions || []).filter((_, i) => i !== ci);
                          setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                        }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    ))}
                  </Box>
                )}

                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
                    onClick={() => {
                      const updated = [...(ec.conditions || []), { field: 'territory', value: '' }];
                      setEmailConfigs((prev) => prev.map((c) => c.id === ec.id ? { ...c, conditions: updated } : c));
                    }}>
                    Add condition
                  </Button>
                  <Button size="small" variant="outlined" onClick={async () => {
                    await updateEmailNotification(clientSlug, ec.id, {
                      name: ec.name, email: ec.email,
                      notify_standard_leads: ec.notify_standard_leads,
                      notify_self_book_leads: ec.notify_self_book_leads,
                      conditions: ec.conditions,
                    });
                    toast.success('Notification saved');
                  }}>
                    Save
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outlined" startIcon={<AddIcon />}
          onClick={async () => {
            const res = await createEmailNotification(clientSlug, {
              name: '', email: '', notify_standard_leads: true, notify_self_book_leads: true, conditions: [],
            });
            if (res?.data) setEmailConfigs((prev) => [...prev, res.data]);
          }}
          sx={{ textTransform: 'none' }}
        >
          Add Email Recipient
        </Button>

        </>}
      </Container>

      {/* Test dialog — edit payload then send */}
      <Dialog open={!!testDialog} onClose={() => { setTestDialog(null); setTestResult(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Test Webhook</Typography>
            <Button
              variant="contained"
              onClick={handleSendTest}
              disabled={testing}
              startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
            >
              {testing ? 'Sending...' : 'Send Test Lead'}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {/* Editable payload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Payload (edit any values before sending)</Typography>
              <TextField
                multiline
                minRows={12}
                maxRows={20}
                fullWidth
                value={testPayloadStr}
                onChange={(e) => setTestPayloadStr(e.target.value)}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                error={(() => { try { if (testPayloadStr) JSON.parse(testPayloadStr); return false; } catch { return true; } })()}
                helperText={(() => { try { if (testPayloadStr) JSON.parse(testPayloadStr); return 'Valid JSON — ready to send'; } catch (e) { return `Invalid JSON: ${e.message}`; } })()}
              />
            </Box>

            {/* Response — shows after sending */}
            {testResult && (
              <>
                <Divider />
                <Alert severity={testResult.success ? 'success' : 'error'} variant="filled">
                  {testResult.success
                    ? `Success! Status ${testResult.status_code}`
                    : `Failed. ${testResult.status_code ? `Status ${testResult.status_code}` : 'Connection error'}`
                  }
                </Alert>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Response Body</Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', bgcolor: '#1e1e1e', borderRadius: 2 }}>
                    <Typography variant="body2" component="pre" sx={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#d4d4d4', m: 0 }}>
                      {testResult.response_body || '(empty response)'}
                    </Typography>
                  </Paper>
                </Box>
              </>
            )}

            {testing && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Posting to webhook...</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ChannelWithStaging({ channel, configs, presets, leadFields, clientVerticals, onSave, onTest, onPromote, viewMode, onViewModeChange, clientSlug, isStaff }) {
  const liveConfig = configs.find((c) => c.channel === channel.value && c.mode === 'live');
  const stagingConfig = configs.find((c) => c.channel === channel.value && c.mode === 'staging');
  const hasStaging = !!stagingConfig;
  const activeConfig = viewMode === 'staging' ? stagingConfig : liveConfig;

  return (
    <ChannelConfig
      key={`${channel.value}-${viewMode}-${activeConfig?.id || 'new'}`}
      channel={channel}
      config={activeConfig}
      presets={presets}
      leadFields={leadFields}
      clientVerticals={clientVerticals}
      onSave={(data) => onSave({ ...data, mode: viewMode === 'staging' ? 'staging' : 'live' })}
      onTest={onTest}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      hasStaging={hasStaging}
      stagingConfig={stagingConfig}
      onPromote={onPromote}
      clientSlug={clientSlug}
      isStaff={isStaff}
    />
  );
}

function ChannelConfig({ channel, config: existing, presets, leadFields: allLeadFields, clientVerticals, onSave, onTest, viewMode, onViewModeChange, hasStaging, stagingConfig, onPromote, clientSlug, isStaff }) {
  // Filter lead fields: show all non-vertical fields + only vertical-specific fields matching client's verticals
  const leadFields = (allLeadFields || []).filter((f) => {
    if (!f.vertical) return true;
    return (clientVerticals || []).some((v) => v.toLowerCase() === f.vertical.toLowerCase());
  });
  const [expanded, setExpanded] = useState(false);
  const [preset, setPreset] = useState(existing?.crm_preset || '');
  const [url, setUrl] = useState(existing?.webhook_url || '');
  const [fieldMapping, setFieldMapping] = useState(existing?.field_mapping || {});
  const [staticValues, setStaticValues] = useState(existing?.static_values || {});
  const [commentsKey, setCommentsKey] = useState(existing?.comments_key || '');
  const [commentsFields, setCommentsFields] = useState(existing?.comments_fields || []);
  const [customHeaders, setCustomHeaders] = useState(existing?.custom_headers || {});
  const [formEncoded, setFormEncoded] = useState(existing?.send_as_form_encoded || false);
  const [authType, setAuthType] = useState(existing?.auth_type || 'none');
  const [oauth2, setOauth2] = useState({
    token_url: existing?.oauth2_token_url || '',
    client_id: existing?.oauth2_client_id || '',
    client_secret: existing?.oauth2_client_secret || '',
    scope: existing?.oauth2_scope || '',
    grant_type: existing?.oauth2_grant_type || 'client_credentials',
  });
  const [successKeyword, setSuccessKeyword] = useState(existing?.success_keyword || '');
  const [useRawJson, setUseRawJson] = useState(existing?.use_raw_json || false);
  const [rawJsonTemplate, setRawJsonTemplate] = useState(existing?.raw_json_template || '');
  const [showPreview, setShowPreview] = useState(false);
  const [routingRules, setRoutingRules] = useState(existing?.routing_rules || []);
  const [phoneFormat, setPhoneFormat] = useState(existing?.phone_format || 'PLAIN');
  const [datetimeTz, setDatetimeTz] = useState(existing?.datetime_timezone || 'local');
  const [datetimeFmt, setDatetimeFmt] = useState(existing?.datetime_format || 'MM/DD/YYYY HH:MM:SS');
  const [productCodes, setProductCodes] = useState(existing?.vertical_product_codes || {});
  const [saving, setSaving] = useState(false);
  const [testingAuth, setTestingAuth] = useState(false);
  const [authTestResult, setAuthTestResult] = useState(null);

  // New field mapping row
  const [newCrmField, setNewCrmField] = useState('');
  const [newLeadField, setNewLeadField] = useState('');
  // New static value row
  const [newStaticKey, setNewStaticKey] = useState('');
  const [newStaticVal, setNewStaticVal] = useState('');
  // New header row
  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderVal, setNewHeaderVal] = useState('');

  const selectedPreset = presets.find((p) => p.key === preset);

  const handlePresetSelect = (presetKey) => {
    setPreset(presetKey);
    const p = presets.find((pr) => pr.key === presetKey);
    if (p && p.available) {
      setFieldMapping(p.default_field_mapping || {});
      setStaticValues(p.default_static_values || {});
      setCommentsKey(p.default_comments_key || '');
      setCommentsFields(p.default_comments_fields || []);
      setFormEncoded(p.send_as_form_encoded || false);
      if (p.default_url) setUrl(p.default_url);
      if (p.default_headers) setCustomHeaders(p.default_headers);
      if (p.default_success_keyword) setSuccessKeyword(p.default_success_keyword);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      channel: channel.value,
      label: selectedPreset?.name || 'Custom',
      crm_preset: preset || 'custom',
      webhook_url: url,
      field_mapping: fieldMapping,
      static_values: staticValues,
      comments_key: commentsKey,
      comments_fields: commentsFields,
      custom_headers: customHeaders,
      send_as_form_encoded: formEncoded,
      success_keyword: successKeyword,
      phone_format: phoneFormat,
      datetime_timezone: datetimeTz,
      datetime_format: datetimeFmt,
      auth_type: authType,
      oauth2_token_url: oauth2.token_url,
      oauth2_client_id: oauth2.client_id,
      oauth2_client_secret: oauth2.client_secret,
      oauth2_scope: oauth2.scope,
      oauth2_grant_type: oauth2.grant_type,
      use_raw_json: useRawJson,
      raw_json_template: rawJsonTemplate,
      routing_rules: routingRules,
      vertical_product_codes: productCodes,
    });
    setSaving(false);
  };

  const addFieldMapping = () => {
    if (newCrmField && newLeadField) {
      setFieldMapping((prev) => ({ ...prev, [newCrmField]: newLeadField }));
      setNewCrmField('');
      setNewLeadField('');
    }
  };

  const removeFieldMapping = (key) => {
    setFieldMapping((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const addStaticValue = () => {
    const key = newStaticKey || newCrmField;
    if (key) {
      setStaticValues((prev) => ({ ...prev, [key]: newStaticVal }));
      setNewStaticKey('');
      setNewStaticVal('');
    }
  };

  const removeStaticValue = (key) => {
    setStaticValues((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const addHeader = () => {
    if (newHeaderKey) {
      setCustomHeaders((prev) => ({ ...prev, [newHeaderKey]: newHeaderVal }));
      setNewHeaderKey('');
      setNewHeaderVal('');
    }
  };

  const removeHeader = (key) => {
    setCustomHeaders((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600}>{channel.label}</Typography>
            <Typography variant="caption" color="text.secondary">{channel.desc}</Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
            <Chip
              label="Live"
              size="small"
              color={viewMode === 'live' ? 'success' : 'default'}
              variant={viewMode === 'live' ? 'filled' : 'outlined'}
              onClick={(e) => { e.stopPropagation(); onViewModeChange('live'); }}
              sx={{ cursor: 'pointer', fontWeight: viewMode === 'live' ? 700 : 400 }}
            />
            <Chip
              label={hasStaging ? 'Staging' : '+ Staging'}
              size="small"
              color={viewMode === 'staging' ? 'warning' : 'default'}
              variant={viewMode === 'staging' ? 'filled' : 'outlined'}
              onClick={(e) => { e.stopPropagation(); onViewModeChange('staging'); }}
              sx={{ cursor: 'pointer', fontWeight: viewMode === 'staging' ? 700 : 400 }}
            />
          </Stack>
          {existing && (
            <Chip
              icon={existing.last_test_success ? <CheckCircleIcon /> : existing.last_test_success === false ? <ErrorIcon /> : null}
              label={existing.webhook_url ? 'Configured' : 'Not set'}
              size="small"
              color={existing.webhook_url ? 'success' : 'default'}
              variant="outlined"
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          {viewMode === 'staging' && (
            <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}
              action={stagingConfig?.id ? (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<RocketLaunchIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onPromote(stagingConfig.id, channel.value)}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  Promote to Live
                </Button>
              ) : null}
            >
              <Typography variant="body2">
                <strong>Staging</strong> — this config will not receive real leads. Test it, then promote when ready.
              </Typography>
            </Alert>
          )}
          {channel.value === 'selfbook' && !existing?.webhook_url && (
            <Alert severity="info">
              <strong>Most clients don't need this.</strong> Self-book leads are automatically sent to your primary webhook with all appointment details included. Only set up a separate self-book webhook if your CRM requires a completely different URL or field mapping for appointment leads.
            </Alert>
          )}
          {channel.value === 'secondary' && !existing?.webhook_url && (
            <Alert severity="info">
              This is for sending lead copies to an additional system (e.g., Hatch, a secondary CRM). Your primary webhook handles all lead delivery — this is optional.
            </Alert>
          )}

          {/* CRM Preset Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Select your CRM / Platform</Typography>
            <Grid container spacing={1}>
              {presets.map((p) => (
                <Grid item key={p.key}>
                  <Chip
                    label={p.available ? p.name : `${p.name} (Coming Soon)`}
                    onClick={p.available ? () => handlePresetSelect(p.key) : undefined}
                    color={p.key === 'custom' ? (preset === 'custom' ? 'secondary' : 'default') : preset === p.key ? 'primary' : 'default'}
                    variant={preset === p.key ? 'filled' : 'outlined'}
                    disabled={!p.available}
                    icon={p.key === 'custom' ? <SettingsIcon sx={{ fontSize: 16 }} /> : undefined}
                    sx={p.key === 'custom' ? { borderStyle: 'dashed' } : {}}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Webhook URL */}
          <TextField
            label="Webhook URL"
            placeholder={selectedPreset?.url_placeholder || 'https://your-api.com/webhook'}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fullWidth
            helperText={selectedPreset?.url_help || 'The URL where leads will be sent via HTTP POST'}
          />

          {/* Success Keyword — staff only */}
          {isStaff && (
            <TextField
              label="Success Keyword (optional)"
              placeholder={selectedPreset?.default_success_keyword || 'e.g., OK, success, id'}
              value={successKeyword}
              onChange={(e) => setSuccessKeyword(e.target.value)}
              fullWidth
              size="small"
              helperText="If set, the webhook response must contain this keyword to count as a successful delivery. Leave blank to use HTTP status code only."
            />
          )}

          {/* Preset-specific required fields (e.g., Hatch source, LP srs_id) */}
          {selectedPreset?.configurable_static_fields?.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Required Settings</Typography>
              <Grid container spacing={2}>
                {selectedPreset.configurable_static_fields.map((f) => (
                  <Grid item xs={12} sm={6} key={f.key}>
                    <TextField
                      label={f.label}
                      value={staticValues[f.key] || ''}
                      onChange={(e) => setStaticValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      fullWidth
                      size="small"
                      required={f.required}
                      helperText={f.help}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Preset-specific required headers (e.g., Hatch Bearer token) */}
          {selectedPreset?.configurable_headers?.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Authentication</Typography>
              <Grid container spacing={2}>
                {selectedPreset.configurable_headers.map((h) => (
                  <Grid item xs={12} key={h.key}>
                    <TextField
                      label={h.label}
                      value={(customHeaders[h.key] || '').replace(h.prefix || '', '')}
                      onChange={(e) => {
                        const val = h.prefix ? `${h.prefix}${e.target.value.replace(h.prefix, '')}` : e.target.value;
                        setCustomHeaders((prev) => ({ ...prev, [h.key]: val }));
                      }}
                      fullWidth
                      size="small"
                      required={h.required}
                      helperText={h.help}
                      placeholder={h.prefix ? `${h.prefix}your-token-here` : 'Value'}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Authentication — only for custom or presets without built-in auth */}
          {(!selectedPreset?.configurable_headers?.length) && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Authentication</Typography>
              <TextField
                select size="small" value={authType}
                onChange={(e) => setAuthType(e.target.value)}
                fullWidth sx={{ mb: 2 }}
              >
                <MenuItem value="none">None (no authentication)</MenuItem>
                <MenuItem value="bearer">Bearer Token (static API key)</MenuItem>
                <MenuItem value="oauth2">OAuth2 Client Credentials (auto-refresh token)</MenuItem>
              </TextField>

              {authType === 'bearer' && (
                <TextField
                  label="Bearer Token"
                  value={(customHeaders['Authorization'] || '').replace('Bearer ', '')}
                  onChange={(e) => setCustomHeaders((prev) => ({ ...prev, Authorization: `Bearer ${e.target.value}` }))}
                  fullWidth size="small"
                  helperText="Your API key or static token"
                  placeholder="your-api-key-here"
                />
              )}

              {authType === 'oauth2' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField label="Token Endpoint URL" value={oauth2.token_url}
                      onChange={(e) => setOauth2((p) => ({ ...p, token_url: e.target.value }))}
                      fullWidth size="small" placeholder="https://api.example.com/oauth/token"
                      helperText="The OAuth2 token endpoint where we request access tokens"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Client ID" value={oauth2.client_id}
                      onChange={(e) => setOauth2((p) => ({ ...p, client_id: e.target.value }))}
                      fullWidth size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Client Secret" type="password" value={oauth2.client_secret}
                      onChange={(e) => setOauth2((p) => ({ ...p, client_secret: e.target.value }))}
                      fullWidth size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Scope (optional)" value={oauth2.scope}
                      onChange={(e) => setOauth2((p) => ({ ...p, scope: e.target.value }))}
                      fullWidth size="small" placeholder="leads.write"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Grant Type" value={oauth2.grant_type}
                      onChange={(e) => setOauth2((p) => ({ ...p, grant_type: e.target.value }))}
                      fullWidth size="small" placeholder="client_credentials"
                      helperText="Usually client_credentials. Change only if your CRM requires a different grant type."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={1.5}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={testingAuth || !existing?.id || !oauth2.token_url}
                        startIcon={testingAuth ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                        onClick={async () => {
                          setTestingAuth(true);
                          setAuthTestResult(null);
                          try {
                            const res = await testAuthWebhookConfig(clientSlug, existing.id);
                            setAuthTestResult(res?.data);
                          } catch (err) {
                            setAuthTestResult({
                              success: false,
                              status_code: err?.response?.status,
                              response: err?.response?.data?.response || err?.response?.data?.detail || 'Request failed',
                            });
                          } finally {
                            setTestingAuth(false);
                          }
                        }}
                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                      >
                        {testingAuth ? 'Testing...' : 'Test Authentication'}
                      </Button>
                      {!existing?.id && oauth2.token_url && (
                        <Typography variant="caption" color="text.secondary">Save the configuration first to test authentication.</Typography>
                      )}
                      {authTestResult && (
                        <Alert severity={authTestResult.success ? 'success' : 'error'} variant="outlined" sx={{ py: 0.5 }}>
                          {authTestResult.success ? (
                            <Box>
                              <Typography variant="body2"><strong>Token received!</strong> Status {authTestResult.status_code}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Token: {authTestResult.token_preview} | Expires in: {authTestResult.expires_in}s
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2">
                                <strong>Failed.</strong> {authTestResult.status_code ? `Status ${authTestResult.status_code}` : 'Connection error'}
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: '#1e1e1e', borderRadius: 1, maxHeight: 120, overflow: 'auto' }}>
                                <Typography variant="body2" component="pre" sx={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#d4d4d4', m: 0 }}>
                                  {typeof authTestResult.response === 'object' ? JSON.stringify(authTestResult.response, null, 2) : authTestResult.response}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                        </Alert>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* Vertical Product Codes */}
          {clientVerticals.length > 0 && channel.value === 'primary' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Product Codes by Vertical</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Enter your CRM's product code for each vertical. This is passed as the product ID when leads are delivered.
              </Typography>
              <Grid container spacing={1.5}>
                {clientVerticals.map((v) => (
                  <Grid item xs={12} sm={6} key={v}>
                    <TextField
                      label={v}
                      placeholder={`Product code for ${v}`}
                      value={productCodes[v] || ''}
                      onChange={(e) => setProductCodes((prev) => ({ ...prev, [v]: e.target.value }))}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Unified Field Mapping — lead fields + static values in one table */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">Field Mapping</Typography>
              <Button
                size="small"
                variant={useRawJson ? 'contained' : 'outlined'}
                color={useRawJson ? 'warning' : 'inherit'}
                onClick={() => {
                  if (!useRawJson && Object.keys(fieldMapping).length > 0) {
                    // Convert current mapping to raw JSON as starting point
                    const payload = {};
                    Object.entries(fieldMapping).forEach(([k, v]) => { payload[k] = `{${v}}`; });
                    Object.entries(staticValues).forEach(([k, v]) => { payload[k] = v; });
                    setRawJsonTemplate(JSON.stringify(payload, null, 2));
                  }
                  setUseRawJson(!useRawJson);
                }}
                startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                {useRawJson ? 'Switch to Visual Editor' : 'Raw JSON Mode'}
              </Button>
            </Stack>

            {useRawJson ? (
              <Box>
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                  <strong>Advanced mode.</strong> Edit the raw JSON payload template directly. Use <code>{'{field_name}'}</code> placeholders for lead data. Static text is sent as-is.
                </Alert>

                {/* Available placeholders */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Available placeholders (click to copy):</Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                    {leadFields.map((f) => (
                      <Chip
                        key={f.key} size="small" variant="outlined"
                        label={`{${f.key}}`}
                        onClick={() => { navigator.clipboard.writeText(`{${f.key}}`); toast.success(`Copied {${f.key}}`); }}
                        sx={{ fontSize: 11, fontFamily: 'monospace', cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Box>

                <TextField
                  multiline minRows={12} maxRows={24} fullWidth
                  value={rawJsonTemplate}
                  onChange={(e) => setRawJsonTemplate(e.target.value)}
                  placeholder={'{\n  "firstName": "{first_name}",\n  "lastName": "{last_name}",\n  "details": {\n    "zip": "{zip_code}"\n  }\n}'}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  error={(() => {
                    if (!rawJsonTemplate) return false;
                    try { JSON.parse(rawJsonTemplate); } catch { return true; }
                    return false;
                  })()}
                  helperText={(() => {
                    if (!rawJsonTemplate) return '';
                    try { JSON.parse(rawJsonTemplate); } catch (e) { return `Invalid JSON: ${e.message}`; }
                    // Check for invalid placeholders
                    const validKeys = new Set(leadFields.map((f) => f.key));
                    const usedPlaceholders = rawJsonTemplate.match(/\{([a-z_]+)\}/g) || [];
                    const invalid = usedPlaceholders
                      .map((p) => p.slice(1, -1))
                      .filter((k) => !validKeys.has(k));
                    if (invalid.length > 0) return `Unknown placeholder(s): {${invalid.join('}, {')}} — check spelling`;
                    return `Valid JSON with ${usedPlaceholders.length} placeholder(s)`;
                  })()}
                />
              </Box>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Map your CRM fields to our lead data, or set static values that are sent with every lead. Click any value to edit.
                </Typography>
            {(Object.keys(fieldMapping).length > 0 || Object.keys(staticValues).length > 0) && (
              <Table size="small" sx={{ mb: 2, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '25%' }}>Your CRM Field <Chip label="editable" size="small" sx={{ fontSize: 10, height: 18, ml: 0.5 }} /></TableCell>
                    <TableCell>Value <Chip label="editable" size="small" sx={{ fontSize: 10, height: 18, ml: 0.5 }} /></TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Dynamic field mappings */}
                  {Object.entries(fieldMapping).map(([crmField, leadField]) => {
                    const isPhone = leadField === 'phone';
                    const isDatetime = leadField === 'self_book_appointment_date_time';
                    const fieldInfo = leadFields.find((f) => f.key === leadField);
                    return (
                      <TableRow key={`f-${crmField}`}>
                        <TableCell>
                          <TextField
                            size="small" variant="standard" value={crmField}
                            onChange={(e) => {
                              const newKey = e.target.value;
                              if (newKey && newKey !== crmField) {
                                setFieldMapping((prev) => {
                                  const n = {};
                                  Object.entries(prev).forEach(([k, v]) => { n[k === crmField ? newKey : k] = v; });
                                  return n;
                                });
                              }
                            }}
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label="Lead Field" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                              <TextField
                                select size="small" variant="standard" value={leadField}
                                onChange={(e) => setFieldMapping((prev) => ({ ...prev, [crmField]: e.target.value }))}
                                sx={{ minWidth: 140 }}
                              >
                                {leadFields.map((f) => (
                                  <MenuItem key={f.key} value={f.key}>
                                    {f.label}
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{f.example}</Typography>
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                            {isPhone && (
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
                                <Typography variant="caption" color="text.secondary">Format:</Typography>
                                <TextField
                                  select size="small" variant="standard" value={phoneFormat}
                                  onChange={(e) => setPhoneFormat(e.target.value)}
                                  sx={{ minWidth: 150, fontSize: 12 }}
                                >
                                  <MenuItem value="PLAIN">8585551234 (digits only)</MenuItem>
                                  <MenuItem value="INTERNATIONAL">+18585551234 (E.164)</MenuItem>
                                  <MenuItem value="NANP">858-555-1234 (dashes)</MenuItem>
                                  <MenuItem value="US_FORMAT">(858) 555-1234 (parens)</MenuItem>
                                </TextField>
                              </Stack>
                            )}
                            {isDatetime && (
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pl: 1 }}>
                                <Typography variant="caption" color="text.secondary">Format:</Typography>
                                <TextField
                                  select size="small" variant="standard" value={datetimeFmt}
                                  onChange={(e) => setDatetimeFmt(e.target.value)}
                                  sx={{ minWidth: 150, fontSize: 12 }}
                                >
                                  <MenuItem value="MM/DD/YYYY HH:MM:SS">04/15/2026 10:00:00</MenuItem>
                                  <MenuItem value="MM/DD/YYYY hh:mm A">04/15/2026 10:00 AM</MenuItem>
                                  <MenuItem value="YYYY-MM-DD HH:MM:SS">2026-04-15 10:00:00</MenuItem>
                                  <MenuItem value="YYYY-MM-DD HH:MM">2026-04-15 10:00</MenuItem>
                                  <MenuItem value="ISO8601">2026-04-15T10:00:00</MenuItem>
                                  <MenuItem value="UNIX">1744732800 (unix)</MenuItem>
                                  <MenuItem value="MONTH_NAME">April 15, 2026 10:00 AM</MenuItem>
                                  <MenuItem value="MM/DD/YYYY">04/15/2026 (date only)</MenuItem>
                                  <MenuItem value="YYYY-MM-DD">2026-04-15 (date only)</MenuItem>
                                  <MenuItem value="MM-DD-YYYY HH:MM:SS">04-15-2026 10:00:00</MenuItem>
                                  <MenuItem value="DD/MM/YYYY HH:MM:SS">15/04/2026 10:00:00</MenuItem>
                                </TextField>
                                <Typography variant="caption" color="text.secondary">TZ:</Typography>
                                <TextField
                                  select size="small" variant="standard" value={datetimeTz}
                                  onChange={(e) => setDatetimeTz(e.target.value)}
                                  sx={{ minWidth: 80, fontSize: 12 }}
                                >
                                  <MenuItem value="local">Local</MenuItem>
                                  <MenuItem value="utc">UTC</MenuItem>
                                </TextField>
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeFieldMapping(crmField)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Static values */}
                  {Object.entries(staticValues).map(([key, val]) => (
                    <TableRow key={`s-${key}`}>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" value={key}
                          onChange={(e) => {
                            const newKey = e.target.value;
                            if (newKey && newKey !== key) {
                              setStaticValues((prev) => {
                                const n = {};
                                Object.entries(prev).forEach(([k, v]) => { n[k === key ? newKey : k] = v; });
                                return n;
                              });
                            }
                          }}
                          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label="Static" size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                          <TextField
                            size="small" variant="standard" value={val}
                            onChange={(e) => setStaticValues((prev) => ({ ...prev, [key]: e.target.value }))}
                            placeholder="Static value"
                            InputProps={{ sx: { fontSize: 13 } }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeStaticValue(key)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {/* Add new row */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small" placeholder="CRM field name"
                value={newCrmField} onChange={(e) => setNewCrmField(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                select size="small" value={newLeadField}
                onChange={(e) => setNewLeadField(e.target.value)}
                sx={{ flex: 1 }}
              >
                <MenuItem value="">Select lead field...</MenuItem>
                <MenuItem value="__static__" sx={{ fontStyle: 'italic', color: 'warning.main' }}>Static value (fixed text)</MenuItem>
                <Divider />
                {leadFields.map((f) => (
                  <MenuItem key={f.key} value={f.key}>
                    {f.label}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{f.example}</Typography>
                  </MenuItem>
                ))}
              </TextField>
              {newLeadField === '__static__' && (
                <TextField
                  size="small" placeholder="Static value"
                  value={newStaticVal} onChange={(e) => setNewStaticVal(e.target.value)}
                  sx={{ flex: 1 }}
                />
              )}
              <Button size="small" onClick={() => {
                if (!newCrmField) return;
                if (newLeadField === '__static__') {
                  addStaticValue();
                  setNewCrmField('');
                  setNewLeadField('');
                } else if (newLeadField) {
                  addFieldMapping();
                }
              }} startIcon={<AddIcon />} disabled={!newCrmField || (!newLeadField)}>
                Add
              </Button>
            </Stack>
              </>
            )}
          </Box>

          {/* Form encoding toggle — only for custom preset */}
          {(!preset || preset === 'custom') && (
            <FormControlLabel
              control={<Checkbox size="small" checked={formEncoded} onChange={(e) => setFormEncoded(e.target.checked)} />}
              label={<Typography variant="body2">Send as URL form-encoded (instead of JSON)</Typography>}
            />
          )}

          {/* Routing Rules */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Routing Rules (optional)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Send leads to different URLs based on lead data. For example, route leads to different endpoints by territory or vertical. Leave empty to send all leads to the primary URL above.
            </Typography>

            {routingRules.length > 0 && (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {routingRules.map((rule, idx) => {
                  const overrides = rule.static_overrides || {};
                  const updateRule = (changes) => {
                    const updated = [...routingRules];
                    updated[idx] = { ...updated[idx], ...changes };
                    setRoutingRules(updated);
                  };
                  // All CRM fields from main config for the override dropdown
                  const allCrmFields = [
                    ...Object.keys(fieldMapping),
                    ...Object.keys(staticValues),
                  ].filter((k, i, arr) => arr.indexOf(k) === i); // dedupe

                  return (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={2}>
                        {/* Condition */}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label="IF" size="small" color="info" sx={{ fontWeight: 700 }} />
                          <TextField
                            select size="small" value={rule.field || ''}
                            onChange={(e) => updateRule({ field: e.target.value })}
                            sx={{ minWidth: 150 }}
                            label="Field"
                          >
                            <MenuItem value="territory">Territory</MenuItem>
                            <MenuItem value="vertical">Vertical</MenuItem>
                            <MenuItem value="product_id">Product ID</MenuItem>
                            <MenuItem value="project_type">Project Type</MenuItem>
                          </TextField>
                          <Typography variant="body2" fontWeight={600}>equals</Typography>
                          <TextField
                            size="small" placeholder="e.g., Roofing, North"
                            value={rule.value || ''}
                            onChange={(e) => updateRule({ value: e.target.value })}
                            sx={{ flex: 1 }}
                            label="Value"
                          />
                          <IconButton size="small" onClick={() => setRoutingRules((prev) => prev.filter((_, i) => i !== idx))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>

                        {/* Then override... */}
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Then override:</Typography>

                        {/* URL override */}
                        <TextField
                          size="small"
                          label="Webhook URL (leave empty to use primary)"
                          placeholder={url || 'Uses primary URL'}
                          value={rule.url || ''}
                          onChange={(e) => updateRule({ url: e.target.value })}
                          fullWidth
                        />

                        {/* Field value overrides — add rows as needed */}
                        {Object.entries(overrides).map(([oKey, oVal]) => (
                          <Stack key={oKey} direction="row" spacing={1} alignItems="center">
                            <TextField
                              select size="small" value={oKey}
                              onChange={(e) => {
                                const newOverrides = {};
                                Object.entries(overrides).forEach(([k, v]) => {
                                  newOverrides[k === oKey ? e.target.value : k] = v;
                                });
                                updateRule({ static_overrides: newOverrides });
                              }}
                              sx={{ minWidth: 160 }}
                              label="CRM Field"
                            >
                              {allCrmFields.map((f) => (
                                <MenuItem key={f} value={f}>{f}</MenuItem>
                              ))}
                              <Divider />
                              <MenuItem value="__custom__" sx={{ fontStyle: 'italic' }}>Custom field name...</MenuItem>
                            </TextField>
                            {oKey === '__custom__' ? (
                              <TextField
                                size="small" placeholder="Field name"
                                value="" autoFocus
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    const newOverrides = { ...overrides };
                                    delete newOverrides['__custom__'];
                                    newOverrides[e.target.value] = oVal;
                                    updateRule({ static_overrides: newOverrides });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && e.target.value) {
                                    const newOverrides = { ...overrides };
                                    delete newOverrides['__custom__'];
                                    newOverrides[e.target.value] = oVal;
                                    updateRule({ static_overrides: newOverrides });
                                  }
                                }}
                                sx={{ minWidth: 140 }}
                              />
                            ) : null}
                            <Typography variant="body2">=</Typography>
                            <TextField
                              size="small" placeholder="Override value"
                              value={oVal}
                              onChange={(e) => updateRule({ static_overrides: { ...overrides, [oKey]: e.target.value } })}
                              sx={{ flex: 1 }}
                              label="Value"
                            />
                            <IconButton size="small" onClick={() => {
                              const newOverrides = { ...overrides };
                              delete newOverrides[oKey];
                              updateRule({ static_overrides: newOverrides });
                            }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}

                        <Button
                          size="small" startIcon={<AddIcon />}
                          onClick={() => updateRule({ static_overrides: { ...overrides, '': '' } })}
                          sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                        >
                          Add field override
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            <Button
              size="small" startIcon={<AddIcon />}
              onClick={() => setRoutingRules((prev) => [...prev, { field: 'territory', value: '', url: '', static_overrides: {} }])}
              sx={{ textTransform: 'none' }}
            >
              Add Routing Rule
            </Button>
          </Box>

          {/* Custom Headers */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Custom Headers (optional)</Typography>
            {Object.keys(customHeaders).length > 0 && (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {Object.entries(customHeaders).map(([k, v]) => (
                  <Stack key={k} direction="row" spacing={1} alignItems="center"
                    sx={{ py: 0.75, px: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>{k}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v}
                    </Typography>
                    <IconButton size="small" onClick={() => removeHeader(k)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" placeholder="Header name" value={newHeaderKey} onChange={(e) => setNewHeaderKey(e.target.value)} sx={{ flex: 1 }} />
              <TextField size="small" placeholder="Header value" value={newHeaderVal} onChange={(e) => setNewHeaderVal(e.target.value)} sx={{ flex: 1 }} />
              <Button size="small" onClick={addHeader} startIcon={<AddIcon />} disabled={!newHeaderKey}>Add</Button>
            </Stack>
          </Box>

          {/* Required fields warning */}
          {(() => {
            const mappedLeadFields = new Set(Object.values(fieldMapping));
            const required = ['first_name', 'last_name', 'phone', 'email'];
            const missing = required.filter((f) => !mappedLeadFields.has(f));
            if (missing.length > 0 && Object.keys(fieldMapping).length > 0) {
              return (
                <Alert severity="warning" variant="outlined">
                  Missing commonly required fields: <strong>{missing.map((f) => {
                    const def = leadFields.find((lf) => lf.key === f);
                    return def?.label || f;
                  }).join(', ')}</strong>. Most CRMs require these for lead creation.
                </Alert>
              );
            }
            return null;
          })()}

          {/* Payload Preview */}
          <Box>
            <Button size="small" onClick={() => setShowPreview(!showPreview)} sx={{ textTransform: 'none', mb: 1 }}>
              {showPreview ? 'Hide' : 'Show'} Payload Preview
            </Button>
            {showPreview && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1e1e1e', borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                <Typography component="pre" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#d4d4d4', whiteSpace: 'pre-wrap', m: 0 }}>
                  {JSON.stringify((() => {
                    const preview = {};
                    const setNested = (d, key, value) => {
                      const parts = key.split('.');
                      let cur = d;
                      for (const part of parts.slice(0, -1)) { cur = cur[part] = cur[part] || {}; }
                      cur[parts[parts.length - 1]] = value;
                    };
                    const examples = {};
                    leadFields.forEach((f) => { examples[f.key] = f.example || f.key; });
                    Object.entries(fieldMapping).forEach(([k, v]) => { setNested(preview, k, examples[v] || `{${v}}`); });
                    Object.entries(staticValues).forEach(([k, v]) => { setNested(preview, k, v); });
                    if (commentsKey && commentsFields.length) {
                      setNested(preview, commentsKey, commentsFields.map((f) => `${f}: ${examples[f] || ''}`).join(' | '));
                    }
                    return preview;
                  })(), null, 2)}
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Actions */}
          <Divider />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {existing?.id && (
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => onTest(existing.id)}
                disabled={!url}
              >
                Send Test Lead
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !url}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
